/**
 * The page has a single 3D pickleball drawn by <BallOverlay>. Sections "direct" it:
 * each registers a function that returns where the ball should be (in viewport pixels)
 * while that section is on screen, or null when it has nothing to say.
 * The first non-null pose, by ascending order, wins.
 */

export interface BallPose {
    /** Center, in viewport pixels from the top left. */
    x: number
    y: number
    /** Radius in CSS pixels. */
    radius: number
    opacity?: number
    squashX?: number
    squashY?: number
    /** When set, the ball is only drawn above this viewport y (used for the sunset). */
    clipBottom?: number
    /** Multiplies the idle spin. */
    spin?: number
}

export interface BallViewport {
    width: number
    height: number
    time: number
}

export type BallDirector = (viewport: BallViewport) => BallPose | null

interface Entry {
    order: number
    director: BallDirector
}

const directors = new Map<string, Entry>()

export const setBallDirector = (id: string, order: number, director: BallDirector): (() => void) => {
    directors.set(id, { order, director })

    return () => {
        if (directors.get(id)?.director === director) directors.delete(id)
    }
}

export const resolveBallPose = (viewport: BallViewport): BallPose | null => {
    const entries = [...directors.values()].sort((a, b) => a.order - b.order)

    for (const entry of entries) {
        const pose = entry.director(viewport)
        if (pose) return pose
    }

    return null
}
